import React from "react"
import styled from "styled-components"
import type { colors } from "component_library/styles/theme"

interface BasicTextProps {
  align?: string
  fontColor?: string
  lineHeight?: string
  fontWeight?: string
  underline?: boolean
  textTransform?: string
  width?: string
  widthFixed?: string
  maxWidth?: string
  maxWidthFixed?: string
  fontFamily?: string
  shrink?: boolean
}

export const basicText = styled.div<BasicTextProps>`
  text-align: ${({ align }) => (align ? align : "left")};
  color: ${({ theme, fontColor }) =>
    fontColor ? theme[fontColor] : theme.gray_800};
  line-height: ${({ lineHeight }) => (lineHeight ? lineHeight : "150%")};
  letter-spacing: 0.005em;
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : "normal")};
  flex-shrink: ${({ shrink }) => (shrink ? 1 : 0)};
  text-decoration: ${({ underline }) => (underline ? "underline" : "none")};
  ${({ fontFamily, theme }) =>
    fontFamily && `font-family: ${theme[fontFamily] || fontFamily};`}
  > .highlight {
    color: ${({ theme }) => theme.blue_400};
  }
  u {
    text-decoration: none;
    position: relative;
    &:before {
      content: "";
      position: absolute;
      bottom: 6px;
      z-index: -1;
      left: 0;
      width: 100%;
      background-color: ${({ theme }) => theme.green_200};
    }
  }
`

const XxlHeadline = styled(basicText)`
  font-size: 40px;
  u {
    &:before {
      height: 8px;
    }
  }
`

const XlHeadline = styled(basicText)`
  font-size: 24px;
  u {
    border-bottom: 2px solid
      ${({ theme, fontColor }) =>
        fontColor ? theme[fontColor] : theme.gray_800};
  }
`
const LargeHeadline = styled(basicText)`
  font-size: 18px;
`
const SmallHeadline = styled(basicText)`
  font-size: 16px;
`

const Body = styled(basicText)`
  font-size: 14px;
  text-transform: ${({ textTransform }) => {
    return textTransform ? textTransform : "normal"
  }};
`

const SmallText = styled(basicText)`
  font-size: 12px;
`
const SuperSmall = styled(basicText)`
  font-size: 8px;
`

export type TextProps = BasicTextProps & {
  textType:
    | "2xl"
    | "xl"
    | "large"
    | "small"
    | "body"
    | "smallText"
    | "superSmall"
  children: React.ReactNode
  fontColor?: keyof typeof colors
  fontWeight?: "500" | "600" | "700" | "900"
  as?: keyof JSX.IntrinsicElements
}

/**
 * All of our different ways of showing text, from headlines to labels.
 * @returns {JSX.Element}
 * @constructor
 */
const Text = ({ textType, as, ...props }: TextProps): JSX.Element => {
  const texts = {
    "2xl": XxlHeadline,
    xl: XlHeadline,
    large: LargeHeadline,
    small: SmallHeadline,
    body: Body,
    smallText: SmallText,
    superSmall: SuperSmall,
  }
  const TextTag = texts[textType]
  return <TextTag as={as} {...props} />
}

export default Text
